import React, { FunctionComponent, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { Dimensions } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import {
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { Modal } from '../../organisms/modal/Modal'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CoverageBenefitsSummary } from '../coverageBenefitsSummary/CoverageBenefitsSummary'

export interface CoverageMessagingModalProps {
  setIsModalOpen: (isModalOpen: boolean) => void
  isModalOpen: boolean
  coverageModalTitleText?: React.ReactNode
  coverageModalContents: CostMessagingModalContent[]
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  trackCoverageModalPress: (isOpen: boolean) => void
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  subheaderText?: string
  goToSearchForCare?: () => void
}

const ContentContainer = styled.View<{ theme: ThemeType; isMobileSized: boolean }>(({ theme, isMobileSized }) => ({
  padding: theme.breakpoints.isMobileSized ? `${theme.spacing['24px']} ${theme.spacing['16px']}` : '',
  maxHeight: isMobileSized ? Dimensions.get('window').height * 0.9 : undefined,
  justifyContent: 'space-between',
}))

const CloseIconContainer = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

export const CoverageMessagingModal: FunctionComponent<CoverageMessagingModalProps> = ({
  setIsModalOpen,
  isModalOpen,
  coverageModalTitleText,
  coverageModalContents,
  costMessagingModalCoverageInfo,
  trackCoverageModalPress,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  subheaderText,
  goToSearchForCare,
}) => {
  const { isMobileSized } = useMediaQuerySize()
  const { colors } = useTheme() as ThemeType

  const bottomSheetRef = useRef<BottomSheetGorhom>(null)

  const closingModal = () => {
    trackCoverageModalPress(false)
    setIsModalOpen(false)
  }

  const openBottomSheet = () => {
    bottomSheetRef.current?.expand()
  }

  const closeBottomSheet = () => {
    closingModal()
    bottomSheetRef.current?.close()
  }

  return (
    <Modal
      modalContents={
        <ContentContainer isMobileSized={isMobileSized}>
          <CoverageBenefitsSummary
            testIDPrefix='CoverageMessagingModal'
            topSlot={
              <CloseIconContainer testID={tID('CoverageMessagingModal-closeIcon')} onPress={closeBottomSheet}>
                <CloseIcon size={16} fillColor={colors.iconDefault} />
              </CloseIconContainer>
            }
            coverageModalTitleText={coverageModalTitleText}
            coverageModalContents={coverageModalContents}
            costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
            customerName={customerName}
            numSessions={numSessions}
            numberOfSessionByProgram={numberOfSessionByProgram}
            displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
            displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
            subheaderText={subheaderText}
            primaryButton={{
              text: <FormattedMessage defaultMessage='Got it' description='Cost messaging modal button text' />,
              onPress: closingModal,
              testID: 'CoverageMessagingModal-confirmationButton',
            }}
            secondaryButton={
              goToSearchForCare
                ? {
                    text: (
                      <FormattedMessage defaultMessage='Find care' description='Cost messaging modal button text' />
                    ),
                    onPress: () => {
                      goToSearchForCare()
                      closingModal()
                    },
                    testID: 'CoverageMessagingModal-searchForCareButton',
                  }
                : undefined
            }
          />
        </ContentContainer>
      }
      onRequestClose={closeBottomSheet}
      visible={isModalOpen}
      bottomSheetRef={bottomSheetRef}
      onCloseEnd={closeBottomSheet}
      disableBottomSheet={!isMobileSized}
      onLayout={openBottomSheet}
      scrollable={isMobileSized}
      useDynamicHeightForBottomSheet={isMobileSized}
    />
  )
}
